import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { setOptionValue } from "tsi-common-react/src/apps/configurator/reducers";
import { rootProductSelector } from "tsi-common-react/src/apps/configurator/selectors";
import { RawHTML } from "tsi-common-react/src/components/RawHTML/index";
import {
    IOptionCode,
    IOptionLabel,
    IOptionSingleValue,
    IOptionValue,
} from "tsi-common-react/src/models/catalogue";
import {
    useAppDispatch,
    useAppSelector,
} from "tsi-common-react/src/store/reducers";
import { notEmpty, unique } from "tsi-common-react/src/utils/functional";
import { sortProductOptions } from "tsi-common-react/src/utils/sorting";

import { IReduxState } from "../../../store/reducers.interfaces";

const getOptionValues = (
    state: Pick<IReduxState, "configurator">,
    attributeCode: IOptionCode,
): IOptionSingleValue[] => {
    const rootProduct = rootProductSelector(state.configurator);
    if (!rootProduct) {
        return [];
    }
    const children = rootProduct.children;
    if (!children || children.length <= 0) {
        const attr = rootProduct.attributes[attributeCode];
        if (!attr) {
            return [];
        }
        return Array.isArray(attr.value)
            ? [...attr.value]
            : [attr.value as IOptionSingleValue];
    }
    const values = children
        .reduce<Array<IOptionSingleValue | null>>((memo, child) => {
            const attr = child.attributes[attributeCode];
            return memo.concat(attr ? attr.value : null);
        }, [])
        .filter(notEmpty)
        .filter(unique);
    return sortProductOptions(attributeCode, values);
};

const getOptionGroups = (state: IReduxState, attributeCode: IOptionCode) => {
    const rootProduct = rootProductSelector(state.configurator);
    if (rootProduct) {
        return (
            rootProduct.attributes.product_option_labels?.value[
                attributeCode
            ] || null
        );
    }
    return null;
};

const handleOptionSelectorClick = (
    event: React.MouseEvent<HTMLElement>,
    namespace: string | null,
    attributeCode: IOptionCode,
    value: string,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    event.preventDefault();
    if (!namespace) {
        return;
    }
    dispatch(
        setOptionValue({
            namespace,
            code: attributeCode,
            index: 0,
            totalNumValues: 1,
            value,
        }),
    );
};

const renderLabelGroups = (
    labelConfig: IOptionLabel[],
    optionValues: IOptionSingleValue[],
    selectedValue: IOptionValue | null,
    namespace: string | null,
    attributeCode: IOptionCode,
    dispatch: ReturnType<typeof useAppDispatch>,
): React.ReactNode => {
    return labelConfig.map((labelGroup) => {
        const optionBtns = labelGroup.values.map((option) => {
            // Make sure option is valid
            if (optionValues.indexOf(option) === -1) {
                return null;
            }
            const optionClass = classNames({
                "--active": option === selectedValue,
            });
            return (
                <button
                    key={option}
                    data-value={option}
                    onClick={(e) => {
                        handleOptionSelectorClick(
                            e,
                            namespace,
                            attributeCode,
                            option,
                            dispatch,
                        );
                    }}
                    className={optionClass}
                >
                    {option}
                </button>
            );
        });
        return (
            <nav key={labelGroup.label}>
                {/* TODO(i18n): The colon should be part of the translated string as punctuation varies by language */}
                <h5>{labelGroup.label}:</h5>
                {optionBtns}
            </nav>
        );
    });
};

export const PDPChangeVariantOptionBlock = (props: {
    attributeCode: IOptionCode;
    initialOptionValuesData?: string;
    initialSelectedValue?: string;
}): React.ReactNode => {
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
        setHasMounted(true);
    }, []);

    const dispatch = useAppDispatch();

    const namespace = useAppSelector((state) => {
        const rootProduct = rootProductSelector(state.configurator);
        return rootProduct ? rootProduct.product_class_slug : null;
    });

    const selectedValue = useAppSelector(
        (state) =>
            state.configurator.ui.optionValues[props.attributeCode] || null,
    );

    const optionValues = useAppSelector((state) =>
        getOptionValues(state, props.attributeCode),
    );

    const optionGroups = useAppSelector((state) =>
        getOptionGroups(state, props.attributeCode),
    );

    const productTitle = useAppSelector((state) => {
        const rootProduct = rootProductSelector(state.configurator);
        return rootProduct ? rootProduct.title : null;
    });

    const productDescription = useAppSelector((state) => {
        const rootProduct = rootProductSelector(state.configurator);
        return rootProduct ? rootProduct.description : null;
    });

    // Parse initial data for SSR fallback
    const initialOptionValues: readonly string[] = useMemo(
        () => JSON.parse(props.initialOptionValuesData || "[]"),
        [props.initialOptionValuesData],
    );

    // Use Redux data when root product is loaded
    const useReduxData = hasMounted && namespace !== null;

    if (!useReduxData) {
        // SSR fallback: render simple buttons from initial data
        if (initialOptionValues.length === 0) {
            return null;
        }
        const selectedInitial = props.initialSelectedValue || "";
        return (
            <React.Fragment key={props.attributeCode}>
                <div>
                    <nav>
                        {initialOptionValues.map((value) => {
                            const optionClass = classNames({
                                "--active": value === selectedInitial,
                            });
                            return (
                                <button
                                    key={value}
                                    data-value={value}
                                    className={optionClass}
                                >
                                    {value}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </React.Fragment>
        );
    }

    // Redux-based rendering (existing interactive behavior)
    // Figure out how to group the radio options (based on the product_option_labels attribute)
    const labelConfigReadonly = optionGroups || [];
    const labelConfig = labelConfigReadonly.map((item) => ({
        label: item.label,
        values: [...item.values],
        description: item.description,
    }));
    if (labelConfig.length <= 0) {
        labelConfig.push({
            label: "",
            values: optionValues,
            description: undefined,
        });
    }

    // Render each label group
    const groups = renderLabelGroups(
        labelConfig,
        optionValues,
        selectedValue,
        namespace,
        props.attributeCode,
        dispatch,
    );

    return (
        <React.Fragment key={props.attributeCode}>
            {productTitle && <h1>{productTitle}</h1>}
            {productDescription && <RawHTML html={productDescription} />}
            <div>{groups}</div>
        </React.Fragment>
    );
};
